import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import ChatRoom from './components/ChatRoom';
import Settings from './components/Settings';
import Leaderboard from './components/Leaderboard';
import UserProfile from './components/UserProfile';
import DogePuddle from './components/DogePuddle';
import FAQ from './components/FAQ';
import TermsOfService from './components/TermsOfService';
import AdminPanel from './components/AdminPanel';
import AdminRoute from './components/AdminRoute';
import DogeRain from './components/DogeRain';
import CountdownTimer from './components/CountdownTimer';
import { getAuthToken, setAuthToken, removeAuthToken } from './utils/auth';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanlinesEnabled, setScanlinesEnabled] = useState(false);
  const [retroCursor, setRetroCursor] = useState(false);

  // Load settings from localStorage
  useEffect(() => {
    const savedScanlines = localStorage.getItem('scanlinesEnabled') === 'true';
    const savedCursor = localStorage.getItem('retroCursor') === 'true';
    setScanlinesEnabled(savedScanlines);
    setRetroCursor(savedCursor);
  }, []);

  // Apply retro cursor dynamically to avoid build-time resolution issues
  useEffect(() => {
    if (retroCursor) {
      // Apply cursor style to body and all elements
      const style = document.createElement('style');
      style.id = 'retro-cursor-style';
      style.textContent = `
        body, body * {
          cursor: url('/cursor.png'), auto !important;
        }
        /* Also apply to html for full coverage */
        html, html * {
          cursor: url('/cursor.png'), auto !important;
        }
      `;
      document.head.appendChild(style);
      return () => {
        const existingStyle = document.getElementById('retro-cursor-style');
        if (existingStyle && existingStyle.parentNode) {
          existingStyle.parentNode.removeChild(existingStyle);
        }
      };
    } else {
      // Remove cursor style when disabled
      const existingStyle = document.getElementById('retro-cursor-style');
      if (existingStyle && existingStyle.parentNode) {
        existingStyle.parentNode.removeChild(existingStyle);
      }
    }
  }, [retroCursor]);

  useEffect(() => {
    // Check if user is already logged in
    const token = getAuthToken();
    if (token) {
      // Verify token by fetching user info using api utility (handles auth headers)
      const checkAuth = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            setIsAuthenticated(true);
          } else {
            // Token invalid or expired
            removeAuthToken();
            setIsAuthenticated(false);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth check error:', error);
          // Don't remove token on network errors, just set loading to false
          // User might be offline
        } finally {
          setLoading(false);
        }
      };
      
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token, userData) => {
    setAuthToken(token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    removeAuthToken();
    setUser(null);
    setIsAuthenticated(false);
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-text">Loading Doge Of The Day... Much Wow!</div>
        <div className="loading-doge">
          <img src="/doge.png" alt="Doge" style={{ width: '64px', height: '64px' }} />
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className={`App ${retroCursor ? 'retro-cursor' : ''}`}>
        {scanlinesEnabled && <div className="scanlines" />}
        <DogeRain />
        {isAuthenticated && <CountdownTimer />}
        <Routes>
          <Route
            path="/login"
            element={
              !isAuthenticated ? (
                <Login onLogin={handleLogin} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <ChatRoom user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                <Settings user={user} onUserUpdate={handleUserUpdate} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/pool"
            element={
              isAuthenticated ? (
                <DogePuddle />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/leaderboard"
            element={
              isAuthenticated ? (
                <Leaderboard />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/user/:id"
            element={
              isAuthenticated ? (
                <UserProfile currentUser={user} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/faq"
            element={
              isAuthenticated ? (
                <FAQ />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/tos"
            element={<TermsOfService />}
          />
          <Route
            path="/admin"
            element={
              isAuthenticated ? (
                <AdminRoute>
                  <AdminPanel user={user} />
                </AdminRoute>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;

